// Click group separation (Hamilton, Starkhammar, Gazda & Connor 2021, J. Acoust. Soc. Am. 150:709) as the engine
// runs it (Technical Reference §8), recomputed in the browser from the clicks as they are now, so the animals figure
// and the "at most" count follow the reviewer's edits. Engine defaults:
//   amplitude: peak-to-peak within 3 dB of the previous click → join its group if the intervals agree
//   (|ICI now − ICI before| ≤ 104.2 ms; no interval rule for beaked whales); otherwise look back over the 2nd…30th
//   previous click: the first with similar amplitude → join its group if the spectral correlation C ≥ 0.9
//   (paper eq. 1 on the relative magnitude spectrum of an 802 µs Tukey(0.3) window), else a new group
//   (beaked whales: keep searching further back). Porpoises (NBHF): no spectrum. Groups with < 5 % of the clicks are
//   removed as noise and the grouping is run again without them; groups still ≥ 5 % are counted.
// Not reproduced: the "within 10 % of the trigger level" shortcut (the detector threshold is not in the bundle).
import { fftrans } from './engineCard';
import { ANIMAL_LABELS } from './pulsedModel';

export const CGS_PROFILE = {
  delphinid_pulsed: { win: 0.000802, low: 5000, spectrum: true, ici: 0.1042, keep: false, lag1: false, all: false },
  beaked_whale: { win: 0.0015, low: 10000, spectrum: true, ici: null, keep: true, lag1: true, all: true },
  porpoise_nbhf: { win: 0.000802, low: 90000, spectrum: false, ici: 0.1042, keep: false, lag1: false, all: false },
  sperm_whale: { win: 0.02, low: 2000, spectrum: true, ici: 0.1042, keep: false, lag1: false, all: false },
};

const tukey = (n, a = 0.3) => Array.from({ length: n }, (_, i) => {
  const x = i / (n - 1);
  if (x < a / 2) return 0.5 * (1 + Math.cos(Math.PI * ((2 * x) / a - 1)));
  if (x > 1 - a / 2) return 0.5 * (1 + Math.cos(Math.PI * ((2 * x) / a - 2 / a + 1)));
  return 1;
});

/** clicks: current clicks; acc: makeAccess(piece, sig); returns Map(no -> group (1..), 0 = removed as noise) + count */
export function cgs(clicks, acc, sectionKey) {
  const P = CGS_PROFILE[sectionKey] || CGS_PROFILE.delphinid_pulsed;
  const NOT = new Set(['echo', 'interference', 'machinery', 'vessel']);
  const input = clicks.filter((k) => (P.all ? !NOT.has(k.label) : ANIMAL_LABELS.has(k.label)) && Number.isFinite(k.pp)).sort((a, b) => a.t - b.t);
  const fs = acc ? acc.fs : 0;
  const half = acc ? Math.max(4, Math.round((P.win / 2) * fs)) : 0;
  const w = half ? tukey(2 * half + 1) : null;
  const specCache = new Map();
  const spec = (k) => {
    if (!P.spectrum || !acc) return null;
    if (specCache.has(k.no)) return specCache.get(k.no);
    const sg = acc.bp(k, half); let U = null;
    if (sg) {
      const x = Array.from(sg, (v, i) => v * w[i]);
      let nfft = 1; while (nfft < x.length) nfft <<= 1; nfft = Math.max(256, nfft);
      const { pxx, f } = fftrans(x, nfft, fs);       // (Hann inside fftrans; the band shape is what is compared)
      const band = []; for (let i = 0; i < f.length; i += 1) if (f[i] >= P.low && f[i] <= 0.45 * fs) band.push(pxx[i]);
      let m = 0; band.forEach((v) => { if (v > m) m = v; });
      U = m > 0 ? band.map((v) => v / m) : null;
    }
    specCache.set(k.no, U); return U;
  };
  const corr = (a, b) => { const A = spec(a); const B = spec(b); if (!A || !B) return null;
    let ab = 0; let aa = 0; let bb = 0; for (let i = 0; i < A.length; i += 1) { ab += A[i] * B[i]; aa += A[i] * A[i]; bb += B[i] * B[i]; }
    return ab / Math.max(aa, bb); };
  const sim = (a, b) => Math.abs(a.pp - b.pp) <= 3;

  const run = (list) => {
    const g = new Map(); let next = 1;
    list.forEach((k, i) => {
      if (i === 0) { g.set(k.no, next); next += 1; return; }
      const prev = list[i - 1];
      if (sim(k, prev)) {
        let ok = true;
        if (P.ici !== null && i >= 2) ok = Math.abs((k.t - prev.t) - (prev.t - list[i - 2].t)) <= P.ici;
        if (ok && P.lag1 && P.spectrum) { const c = corr(k, prev); if (c !== null && c < 0.9) ok = false; }
        if (ok) { g.set(k.no, g.get(prev.no)); return; }
      }
      for (let j = 2; j <= 30 && i - j >= 0; j += 1) {
        const q = list[i - j];
        if (!sim(k, q)) continue;
        const c = P.spectrum ? corr(k, q) : 1;
        if (c === null || c >= 0.9) { g.set(k.no, g.get(q.no)); return; }
        if (!P.keep) break;
      }
      g.set(k.no, next); next += 1;
    });
    return g;
  };
  const share = (g, n) => { const cnt = new Map(); g.forEach((v) => cnt.set(v, (cnt.get(v) || 0) + 1)); return [...cnt.entries()].filter(([, c]) => c / n >= 0.05).map(([v]) => v); };
  // first pass, low-percentage removal, second pass without the removed clicks
  const g1 = run(input); const keep1 = new Set(share(g1, input.length));
  const kept = input.filter((k) => keep1.has(g1.get(k.no)));
  const g2 = run(kept); const keep2 = share(g2, kept.length);
  // renumber the counted groups 1..N in order of first click; everything else 0 (removed as noise)
  const order = []; kept.forEach((k) => { const v = g2.get(k.no); if (keep2.includes(v) && !order.includes(v)) order.push(v); });
  const out = new Map();
  input.forEach((k) => { const v = g2.get(k.no); out.set(k.no, v !== undefined && keep2.includes(v) ? order.indexOf(v) + 1 : 0); });
  return { groups: out, count: order.length };
}
